import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import { RecordingScheduleGrid } from "./RecordingScheduleGrid.tsx";
import {
  cloneRecordingScheduleSlots,
  createEmptyRecordingScheduleSlots,
} from "../../lib/recording-schedule.ts";
import { getUserTimeZone } from "../../utils/userTimezoneHeader.js";
import type { RecordingScheduleSlots } from "../../types/recordingSchedule.ts";

export interface AdminRecordingScheduleDialogProps {
  open: boolean;
  onClose: () => void;
  initialSlots?: RecordingScheduleSlots | null;
  initialTimezone?: string | null;
  loading?: boolean;
  loadError?: string | null;
  onSave: (slots: RecordingScheduleSlots, timezone: string) => void;
}

export function AdminRecordingScheduleDialog({
  open,
  onClose,
  initialSlots,
  initialTimezone,
  loading = false,
  loadError = null,
  onSave,
}: AdminRecordingScheduleDialogProps): React.ReactElement {
  const [slots, setSlots] = useState<RecordingScheduleSlots>(() =>
    createEmptyRecordingScheduleSlots(),
  );
  const [timezone, setTimezone] = useState(getUserTimeZone());

  useEffect(() => {
    if (!open) return;
    setSlots(
      initialSlots
        ? cloneRecordingScheduleSlots(initialSlots)
        : createEmptyRecordingScheduleSlots(),
    );
    setTimezone((initialTimezone ?? "").trim() || getUserTimeZone());
  }, [open, initialSlots, initialTimezone]);

  const handleSave = () => {
    onSave(cloneRecordingScheduleSlots(slots), timezone.trim() || getUserTimeZone());
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
    >
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.5,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          borderBottom: "1px solid #E5E7EB",
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 20,
              fontWeight: 600,
              color: "#E85D2C",
            }}
          >
            Weekly recording schedule
          </Typography>
          <Typography
            sx={{
              fontFamily: "Roboto, sans-serif",
              fontSize: 13,
              color: "#6B7280",
              mt: 0.5,
            }}
          >
            Timezone: {timezone}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close" sx={{ color: "#6B7280" }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={32} sx={{ color: "#2932E5" }} />
          </Box>
        ) : loadError ? (
          <Typography sx={{ color: "#B91C1C", fontSize: 14, mb: 2 }}>{loadError}</Typography>
        ) : null}
        {!loading ? <RecordingScheduleGrid value={slots} onChange={setSlots} /> : null}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2, pt: 0, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            borderColor: "#E5E7EB",
            color: "#374151",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{
            textTransform: "none",
            fontFamily: "Roboto, sans-serif",
            bgcolor: "#2932E5",
            "&:hover": { bgcolor: "#1e28b8" },
          }}
        >
          Save schedule
        </Button>
      </DialogActions>
    </Dialog>
  );
}
